import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { queriesApi } from '../services/queriesService'

/**
 * Hook para obtener datos de formularios de desafíos de Contentful
 * Incluye contenido para formularios de retos y desafíos
 *
 * @returns {Object} Objeto con loading, error, datos procesados y funciones utilitarias
 */
export const useContentfulFormChallenge = () => {
	// Query con Tanstack Query
	const {
		data: rawData,
		isLoading,
		error,
		refetch,
		isStale,
		isRefetching
	} = useQuery({
		queryKey: ['contentful', 'form-challenge'] as const,
		queryFn: queriesApi.getFormChallenge,
		staleTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		cacheTime: 5 * 24 * 60 * 60 * 1000, // 5 días
		retry: 2,
		retryDelay: attemptIndex => Math.min(1000 * 2 ** attemptIndex, 30000),
		refetchOnWindowFocus: false,
		refetchOnReconnect: true
	})

	// Manejar errores
	if (error) {
		console.error('Error al obtener datos de Form Challenge desde Contentful:', error)
	}

	// Procesar datos de pregunta de retos
	const preguntaRetos = useMemo(() => {
		if (!rawData || isLoading || error) return null

		try {
			return rawData?.homeCollection?.items?.[0]?.referenciaPreguntasYClases?.preguntaRetos || null
		} catch (transformError) {
			console.error('Error al procesar datos de pregunta de retos:', transformError)
			return null
		}
	}, [rawData, isLoading, error])

	// Información del cache
	const cacheInfo = useMemo(
		() => ({
			isStale,
			isRefetching,
			lastFetch: rawData ? new Date() : null,
			cacheExpiry: new Date(Date.now() + 5 * 24 * 60 * 60 * 1000)
		}),
		[isStale, isRefetching, rawData]
	)

	return {
		// Datos principales (compatibilidad con hook original)
		loading: isLoading,
		preguntaRetos,

		// Funcionalidades adicionales
		error,
		refetch,
		isStale,
		isRefetching,
		cacheInfo,
		rawData
	}
}

export default useContentfulFormChallenge
